import { Box, Flex, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { useMemberSignedIn } from "modules/members/auth";
import { useCanonicalPortalRedirect } from "modules/members/useCanonicalPortalRedirect";
import { Suspense, useRef } from "react";
import { Outlet, RouteObject, useLocation } from "react-router-dom";
import PageSpinner from "ui/PageSpinner";
import ScrollToTop from "components/ScrollToTop";
import ApprovalRequest from "./Access/ApprovalRequests";
import Catalogs from "./Access/Catalogs";
import CatalogIndex from "./Access/Catalogs/CatalogIndex";
import DeprovisioningTask from "./Access/DeprovisioningTasks";
import EntitlementAdjustmentTask from "./Access/EntitlementAdjustmentTasks";
import ExtensionRequest from "./Access/ExtensionRequests";
import ProvisioningTask from "./Access/ProvisioningTasks";
import MemberAccessReviews from "./AccessReviews";
import Attestations from "./Attestations";
import AttestationDetail from "./Attestations/Detail";
import MyApps from "./Apps";
import SignIn from "./Auth/Login";
import MembersAuthSuccess from "./Auth/Success";
import HomeOrFirstNav from "./HomeOrFirstNav";
import MyAccess from "./MyAccess";
import MemberOffboardings from "./Offboarding";
import TasksPage from "./Tasks";
import TopNav from "components/nav/TopNav";
import MobileTabBar from "components/nav/MobileTabBar";
import MFMembers from "state/members/membersState";
import { MemberSocketProvider } from "modules/api/websockets";
import ReviewDetail from "./AccessReviews/campaigns/ReviewDetail";
import LunaSessionPage from "./Luna/LunaSessionPage";
import LunaRequestsPage from "./Luna/LunaRequestsPage";
import CliApproval from "./CliApproval";
import CliSetup from "./CliSetup";
import Passkeys from "./Passkeys";
import StepUp from "./StepUp";
const noPaddingRoutes = ["/luna/requests"];

// Design-system gallery — dev/preview only. In a production build
// `import.meta.env.PROD` is the literal `true`, so the `import()` below sits
// in dead code and Rollup drops the whole _preview chunk from the bundle:
// the gallery is never shipped or routable in prod.
const devOnlyRoutes: RouteObject[] = import.meta.env.PROD
  ? []
  : [
      {
        path: "/members/_ds-preview",
        lazy: async () => {
          const { default: Component } = await import(
            "components/ds/_preview/PreviewPage"
          );
          return { Component };
        },
      },
    ];

const MemberDashboard = () => {
  const signedIn = useMemberSignedIn();
  const location = useLocation();
  // The scroll container on desktop is the document; on mobile it's <main>
  // (see the sx below). ScrollToTop resets both on navigation.
  const mainRef = useRef<HTMLDivElement>(null);
  const noPadding = noPaddingRoutes.some((route) =>
    location.pathname.startsWith(route),
  );
  const data = MFMembers.useGetPortalConfig();

  useCanonicalPortalRedirect(data?.subdomain);

  // Design-system preview route (dev only) renders bare — no sidebar, no
  // socket provider, no auth-gated chrome. The guard compiles away in prod.
  if (
    !import.meta.env.PROD &&
    location.pathname.startsWith("/members/_ds-preview")
  ) {
    return <Outlet />;
  }

  if (!signedIn) {
    return <Outlet />;
  }
  return (
    <Suspense fallback={<PageSpinner />}>
      <MemberSocketProvider />
      <ScrollToTop containerRef={mainRef} />
      {/* Redesigned shell: sticky top nav, full-width content, mobile bottom
          tab bar. Replaces the former left sidebar + mobile hamburger. */}
      {/* Full-height routes (Luna chat) need a DEFINITE viewport height so the
          flex-grown <main> resolves a child's `h="full"` — `minH:100vh` alone is
          indefinite and collapses the chat. Normal routes keep minH so the page
          can grow + scroll. */}
      <Box
        h={noPadding ? "100vh" : undefined}
        minH="100vh"
        bg="neutral.25"
        display="flex"
        flexDirection="column"
        sx={{
          // Mobile: a fixed-height (dynamic-viewport) app-shell. <main> scrolls
          // internally and the bottom tab bar is the last flex child, so the bar
          // stays pinned to the bottom of the screen and page content always
          // scrolls *above* it — nothing is ever hidden behind it. Overrides the
          // desktop document-scroll height props above. Desktop is unchanged.
          "@media (max-width: 767px)": {
            height: "100dvh",
            minHeight: "100dvh",
            overflow: "hidden",
          },
        }}
      >
        <TopNav />
        <Box
          as="main"
          ref={mainRef}
          flex="1"
          minH={0}
          w="full"
          overflow={noPadding ? "hidden" : undefined}
          sx={
            noPadding
              ? // Full-height routes (e.g. the Luna chat) need <main> to be a
                // flex column so the page's `h="full"` actually fills — a
                // block parent's flex-grown height doesn't reliably resolve a
                // child's percentage height, collapsing the chat view.
                { padding: 0, display: "flex", flexDirection: "column" }
              : {
                  // Design `.page` gutters (40/24/16) + bottom breathing room;
                  // mobile reserves space for the fixed bottom tab bar.
                  paddingTop: "24px",
                  paddingLeft: "40px",
                  paddingRight: "40px",
                  paddingBottom: "80px",
                  "@media (max-width: 1024px)": {
                    paddingLeft: "24px",
                    paddingRight: "24px",
                  },
                  "@media (max-width: 767px)": {
                    // Mobile app-shell: <main> is the scroll container (the
                    // outer shell is fixed-height + clipped). The tab bar is a
                    // flex sibling below, so we no longer reserve space to clear
                    // a fixed bar — just normal bottom breathing room.
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    paddingTop: "16px",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingBottom: "24px",
                  },
                }
          }
        >
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </Box>
        <MobileTabBar />
      </Box>
    </Suspense>
  );
};

export const membersRoutes: RouteObject[] = [
  {
    Component: MemberDashboard,
    ErrorBoundary: () => {
      return (
        <HStack align="flex-start" minW="100vw" minH="100vh">
          <VStack width="100%" spacing={4}>
            <VStack spacing={0}>
              <Text fontSize="5xl" fontWeight="500">
                Oops.
              </Text>
              <Text fontSize="2xl">Looks like we've made a mistake.</Text>
            </VStack>
            <Text>Sorry about this, we should have it fixed soon.</Text>
          </VStack>
        </HStack>
      );
    },
    children: [
      // Top-level, not under /members, because a person types this one off a
      // terminal: `ploy login` tells them to open <portal>/cli.
      {
        path: "/cli",
        Component: CliApproval,
      },
      {
        path: "/cli/setup",
        Component: CliSetup,
      },
      {
        path: "/luna/requests",
        Component: LunaRequestsPage,
      },
      {
        path: "/luna/requests/:sessionId/*",
        Component: LunaSessionPage,
      },
      {
        path: "/members/access/catalogs/:id/:item_id",
        Component: () => (
          <Suspense
            fallback={
              <Flex h="100vh" w="full" justify="center" align="center">
                <Spinner />
              </Flex>
            }
          >
            <Catalogs />
          </Suspense>
        ),
      },
      {
        path: "/members/access/catalogs/:id",
        Component: () => (
          <Suspense
            fallback={
              <Flex h="100vh" w="full" justify="center" align="center">
                <Spinner />
              </Flex>
            }
          >
            <Catalogs />
          </Suspense>
        ),
      },
      {
        path: "/members/catalog",
        Component: CatalogIndex,
      },
      {
        path: "/",
        Component: HomeOrFirstNav,
      },
      {
        path: "/members/",
        Component: HomeOrFirstNav,
      },
      {
        path: "/members/my-access",
        Component: MyAccess,
      },
      {
        path: "/members/apps",
        Component: MyApps,
      },
      {
        path: "/members/extensions/:id",
        Component: ExtensionRequest,
      },
      {
        path: "/members/approval-requests/:id",
        Component: ApprovalRequest,
      },
      {
        path: "/members/provisioning/:id",
        Component: ProvisioningTask,
      },
      {
        path: "/members/deprovisioning/:id",
        Component: DeprovisioningTask,
      },
      {
        path: "/members/entitlement_adjustment/:id",
        Component: EntitlementAdjustmentTask,
      },
      {
        path: "/members/assigned-tasks",
        Component: TasksPage,
      },
      // Signing an access request or an approval with a passkey. The CLI, the
      // extension and the desktop app open this page by challenge id.
      {
        path: "/members/step-up/:challengeId",
        Component: StepUp,
      },
      {
        path: "/members/passkeys",
        Component: Passkeys,
      },
      {
        path: "/members/:member_id/access-reviews",
        Component: MemberAccessReviews,
      },
      {
        path: "/members/access-reviews",
        Component: MemberAccessReviews,
      },
      {
        path: "/members/access-reviews/*",
        Component: MemberAccessReviews,
      },
      {
        path: "/members/access-reviews/reviews/:reviewId",
        Component: ReviewDetail,
      },
      {
        path: "/members/attestations",
        Component: Attestations,
      },
      {
        path: "/members/attestations/:id",
        Component: AttestationDetail,
      },
      {
        path: "/members/:member_id/offboarding",
        Component: MemberOffboardings,
      },
      {
        path: "/members/offboarding",
        Component: MemberOffboardings,
      },
      {
        path: "/members/auth/success",
        Component: MembersAuthSuccess,
      },
      {
        path: "/members/auth/sign-in",
        Component: SignIn,
      },
      ...devOnlyRoutes,
    ],
  },
];
